import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatDate, formatINR } from "@/lib/constants";
import { ShieldCheck, CheckCircle2 } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

export default function PlanClientView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approved, setApproved] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/public/plans/${token}`);
      setData(res.data);
      if (res.data.plan.status === "Approved") setApproved(true);
    } catch {
      toast.error("Plan link not available");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [token]);

  const approve = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/plans/${token}/approve`, { client_signoff_method: "in-app" });
      toast.success("Plan approved. Thank you.");
      setApproved(true);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approval failed");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-10 text-center">This plan is not available.</div>;

  const { plan, advice_items, client_name } = data;
  const investmentSide = advice_items.filter((a) => ["Emergency Fund", "Surplus Investment Strategy", "Retirement & SWP", "Debt Management"].includes(a.category));
  const protectionSide = advice_items.filter((a) => ["Health Insurance", "Term Insurance"].includes(a.category));

  return (
    <div className="marketing hero-bg min-h-screen">
      <header className="border-b border-black/5 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center gap-2 px-6 py-4">
          <BrandLogo size="sm" />
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="mb-6 flex items-center gap-3 text-sm text-primary">
          <ShieldCheck className="h-4 w-4" />
          Your Written Plan · Version {plan.version_number} · For your review before you commit to anything.
        </div>
        <h1 className="mb-3 text-3xl tracking-tight sm:text-4xl">Your plan, {client_name.split(" ")[0]}.</h1>
        <p className="mb-10 text-muted-foreground">
          Two sides, one strategy — the investments to build toward your goals, and the protection to safeguard them.
        </p>

        <div className="mb-8 grid gap-6 md:grid-cols-2">
          <Card className="border-primary/30 bg-white p-6" data-testid="plan-investment-card">
            <div className="overline mb-3 text-primary">Investment plan</div>
            <p className="mb-6 whitespace-pre-line text-sm leading-relaxed">{plan.investment_plan_summary}</p>
            <ul className="space-y-3">
              {investmentSide.map((a) => (
                <li key={a.id} className="rounded-md border border-border bg-secondary/40 p-3 text-sm">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="font-semibold">{a.category}</span>
                    {a.recommended_amount > 0 && <span className="text-xs text-primary">{formatINR(a.recommended_amount)}</span>}
                  </div>
                  <div>{a.recommendation_text}</div>
                  {a.recommended_instrument && <div className="mt-1 text-xs text-muted-foreground">Instrument · {a.recommended_instrument}</div>}
                </li>
              ))}
              {investmentSide.length === 0 && <li className="text-xs text-muted-foreground">No investment items in this plan.</li>}
            </ul>
          </Card>

          <Card className="border-accent/40 bg-white p-6" data-testid="plan-protection-card">
            <div className="overline mb-3 text-accent">Protection plan</div>
            <p className="mb-6 whitespace-pre-line text-sm leading-relaxed">{plan.protection_plan_summary}</p>
            <ul className="space-y-3">
              {protectionSide.map((a) => (
                <li key={a.id} className="rounded-md border border-border bg-secondary/40 p-3 text-sm">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="font-semibold">{a.category}</span>
                    {a.recommended_amount > 0 && <span className="text-xs text-accent">{formatINR(a.recommended_amount)}</span>}
                  </div>
                  <div>{a.recommendation_text}</div>
                  {a.recommended_instrument && <div className="mt-1 text-xs text-muted-foreground">Instrument · {a.recommended_instrument}</div>}
                </li>
              ))}
              {protectionSide.length === 0 && <li className="text-xs text-muted-foreground">No protection items in this plan.</li>}
            </ul>
          </Card>
        </div>

        {approved ? (
          <Card className="border-primary/40 bg-primary/5 p-6 text-center" data-testid="plan-approved-card">
            <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-primary" />
            <h2 className="mb-1 font-dashboard text-xl font-bold">Plan approved</h2>
            <p className="text-sm text-muted-foreground">
              Thanks. We&apos;ll now begin execution — cleanly, and only through instruments purpose-built for each
              recommendation. Approved on {formatDate(plan.approved_at)}.
            </p>
          </Card>
        ) : (
          <Card className="border-border bg-white p-6">
            <h2 className="mb-2 font-dashboard text-xl font-bold">Ready to proceed?</h2>
            <p className="mb-6 text-sm text-muted-foreground">
              Approving this plan authorises us to begin execution. You can pause at any time, and any change of
              circumstance triggers a review.
            </p>
            <Button onClick={approve} disabled={busy} className="gap-2" data-testid="approve-plan-btn">
              <CheckCircle2 className="h-4 w-4" /> {busy ? "Approving…" : "I approve this plan"}
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}
